import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEO from '../../components/feature/SEO';

export default function PaymentFree() {
  const navigate = useNavigate();

  useEffect(() => {
    // Add confetti effect
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const colors = ['#29B3E6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

    const randomInRange = (min: number, max: number) => {
      return Math.random() * (max - min) + min;
    };

    const confettiInterval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        clearInterval(confettiInterval);
        return;
      }

      const particleCount = 3;

      for (let i = 0; i < particleCount; i++) {
        const particle = document.createElement('div');
        particle.style.position = 'fixed';
        particle.style.width = '10px';
        particle.style.height = '10px';
        particle.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
        particle.style.left = Math.random() * window.innerWidth + 'px';
        particle.style.top = '-20px';
        particle.style.opacity = '1';
        particle.style.borderRadius = '50%';
        particle.style.pointerEvents = 'none';
        particle.style.zIndex = '9999';
        document.body.appendChild(particle);

        const fallDuration = randomInRange(2000, 4000);
        const startTime = Date.now();

        const animate = () => {
          const elapsed = Date.now() - startTime;
          const progress = elapsed / fallDuration;

          if (progress < 1) {
            particle.style.top = progress * window.innerHeight + 'px';
            particle.style.opacity = String(1 - progress);
            requestAnimationFrame(animate);
          } else {
            document.body.removeChild(particle);
          }
        };

        animate();
      }
    }, 50);

    return () => clearInterval(confettiInterval);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-teal-50">
      <SEO title="Free Account Activated – QBOVerifier" description="Your QBOVerifier free account is ready. Complete registration to get started." noIndex />
      <Header />
      
      <div className="py-20 px-6">
        <div className="max-w-3xl mx-auto">
          {/* Success Icon with Animation */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-32 h-32 bg-gradient-to-br from-green-400 to-green-600 rounded-full mb-6 animate-bounce shadow-2xl">
              <i className="ri-checkbox-circle-line text-7xl text-white"></i>
            </div>
            
            <h1 className="text-5xl font-bold text-gray-900 mb-4">
              Welcome to Your Free Account!
            </h1>
            

          </div>

          {/* Success Details Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 border-2 border-green-100">
            <div className="flex items-start gap-4 mb-6 pb-6 border-b border-gray-200">
              <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-full flex-shrink-0">
                <i className="ri-mail-send-line text-2xl text-green-600"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Confirmation Email Sent
                </h3>
                <p className="text-gray-600">
                  We've sent a confirmation email with your receipt. Please check your inbox.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4 mb-6 pb-6 border-b border-gray-200">
              <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-full flex-shrink-0">
                <i className="ri-user-add-line text-2xl text-blue-600"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Account Activation
                </h3>
                <p className="text-gray-600">
                  Your account has been set up. We now need some details that are required for the processing and sending of the bioEncrypted QBOVerifier activated invoice. 
Please continue with the registration process. 
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-purple-100 rounded-full flex-shrink-0">
                <i className="ri-customer-service-2-line text-2xl text-purple-600"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Need Help?
                </h3>
                <p className="text-gray-600">
                  Our support team is here to help you get started. Feel free to reach out if you have any questions.
                </p>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="bg-gradient-to-r from-green-50 to-teal-50 rounded-2xl p-8 mb-8 border-2 border-green-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              What's Next?
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-white rounded-full mx-auto mb-4 shadow-md">
                  <span className="text-2xl font-bold text-green-600">1</span>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Complete Registration</h3>
                <p className="text-sm text-gray-600 text-left">Continue with the Registration so we can complete the setup of your account with your Company details to be used in your secure QBOVerifier account. 
You will be asked to confirm your mobile number and email address. </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-white rounded-full mx-auto mb-4 shadow-md">
                  <span className="text-2xl font-bold text-green-600">2</span>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Install QBOVerifiers</h3>
                <p className="text-sm text-gray-600 text-left">Install the QBOVerifier browser extension that automatically generates your new QBOVerifier invoice emails. You will be provided with instructions.</p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-white rounded-full mx-auto mb-4 shadow-md">
                  <span className="text-2xl font-bold text-green-600">3</span>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Start Verifying</h3>
                <p className="text-sm text-gray-600 text-left">Begin using QBOVerifier today</p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <form
              method="POST"
              action="https://id.xactid.com/biomailenroll/web/issue-token.asp"
              className="contents"
            >
              <input type="hidden" name="method" value="free" />
              <input type="hidden" name="site" value="qboVerifier" />
              <button
                type="submit"
                className="px-8 py-4 text-white rounded-lg font-semibold text-lg transition-all hover:shadow-xl whitespace-nowrap text-center cursor-pointer"
                style={{ backgroundColor: '#29B3E6' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563EB'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#29B3E6'}
              >
                <i className="ri-login-box-line mr-2"></i>
                Complete Registration
              </button>
            </form>

            <a
              href="/"
              className="px-8 py-4 bg-white text-gray-700 rounded-lg font-semibold text-lg border-2 border-gray-300 hover:border-gray-400 hover:shadow-lg transition-all whitespace-nowrap cursor-pointer text-center"
            >
              <i className="ri-home-line mr-2"></i>
              Return to Home
            </a>
          </div>

          {/* Support Contact */}
          <div className="text-center mt-12 pt-8 border-t border-gray-200">
            <p className="text-gray-600 mb-4">
              Questions about your purchase?
            </p>
            <a
              href="/contact"
              className="text-lg font-semibold hover:underline cursor-pointer whitespace-nowrap"
              style={{ color: '#29B3E6' }}
            >
              Contact Our Support Team
              <i className="ri-arrow-right-line ml-2"></i>
            </a>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
